import React, { useEffect, useMemo, useState } from 'react';
import { Globe } from 'lucide-react';
import { apiPublica } from './apiPublica';
import MiniMes from '../../components/calendar/MiniMes';
import {
  capital, fmtHora, hoyEn, inicioMes, listaZonas, nombreZona, rejillaMes, sumarDias, textoDiaLargo, ymdDe,
} from '../../lib/calendario';

/**
 * Mes con los días que tienen huecos + horas libres del día elegido, todo en la zona del VISITANTE.
 * El backend da los huecos por fechas LOCALES de la página (`desde`/`hasta`): se pide el mes con un día de
 * margen por cada lado y se reparten por día en la zona del visitante.
 */
export default function SelectorHuecos({ slug, ventanaDias = 60, zona, onZona, onElegir, elegido, recarga = 0, color }) {
  const hoy = hoyEn(zona);
  const [mes, setMes] = useState(inicioMes(hoy));
  const [dia, setDia] = useState(null);
  const [estado, setEstado] = useState({ cargando: true, huecos: [], error: '' });
  const zonas = useMemo(() => listaZonas(), []);
  const maxDia = sumarDias(hoy, ventanaDias + 1);

  useEffect(() => {
    let vivo = true;
    const rejilla = rejillaMes(mes);
    const desde = sumarDias(rejilla[0] < hoy ? hoy : rejilla[0], -1);
    const hasta = sumarDias(rejilla[41] > maxDia ? maxDia : rejilla[41], 1);
    setEstado((e) => ({ ...e, cargando: true, error: '' }));
    if (hasta < desde) { setEstado({ cargando: false, huecos: [], error: '' }); return undefined; }
    apiPublica.get(`/reservar/${encodeURIComponent(slug)}/huecos`, { params: { desde, hasta } })
      .then(({ data }) => { if (vivo) setEstado({ cargando: false, huecos: data.huecos || [], error: '' }); })
      .catch((e) => { if (vivo) setEstado({ cargando: false, huecos: [], error: e?.response?.data?.detail || 'No se pudieron cargar las horas libres' }); });
    return () => { vivo = false; };
  }, [slug, mes, recarga]); // eslint-disable-line react-hooks/exhaustive-deps

  const porDia = useMemo(() => {
    const m = new Map();
    for (const h of estado.huecos) {
      const d = ymdDe(h, zona);
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(h);
    }
    return m;
  }, [estado.huecos, zona]);
  const conHueco = useMemo(() => new Set(porDia.keys()), [porDia]);

  // Al cargar un mes, se elige el primer día con horas (si el elegido no tiene)
  useEffect(() => {
    if (estado.cargando) return;
    if (dia && porDia.has(dia)) return;
    const primero = [...porDia.keys()].filter((d) => d.slice(0, 7) === mes.slice(0, 7)).sort()[0];
    setDia(primero || null);
  }, [porDia, estado.cargando]); // eslint-disable-line react-hooks/exhaustive-deps

  const horas = dia ? porDia.get(dia) || [] : [];

  return (
    <div className="pub-selector" style={color ? { '--pub-acento': color } : undefined} data-testid="pub-selector">
      <div className="pub-selector-mes">
        <MiniMes
          grande
          valor={dia}
          hoy={hoy}
          mesInicial={mes}
          onCambiarMes={(m) => { setMes(m); setDia(null); }}
          onElegir={(d) => setDia(d)}
          marcados={conHueco}
          deshabilitado={(d) => !conHueco.has(d)}
          minMes={hoy}
          maxMes={maxDia}
        />
        <label className="pub-zona">
          <Globe className="h-4 w-4" />
          <select value={zona} onChange={(e) => onZona(e.target.value)} aria-label="Zona horaria">
            {zonas.map((z) => <option key={z} value={z}>{nombreZona(z)}</option>)}
          </select>
        </label>
      </div>
      <div className="pub-selector-horas">
        {estado.cargando ? <p className="pub-ayuda">Buscando horas libres…</p>
          : estado.error ? <p className="pub-error">{estado.error}</p>
            : !dia ? <p className="pub-ayuda">{conHueco.size ? 'Elige un día.' : 'No quedan horas libres este mes. Prueba con el siguiente.'}</p>
              : (
                <>
                  <p className="pub-horas-titulo">{capital(textoDiaLargo(dia))}</p>
                  <div className="pub-horas" role="list">
                    {horas.map((h) => (
                      // role="listitem" va en un envoltorio: puesto en el botón le quitaba su rol de botón (K14)
                      <span role="listitem" key={h}>
                      <button
                        type="button"
                        className={`pub-hora ${elegido === h ? 'is-activa' : ''}`}
                        onClick={() => onElegir(h)}
                        aria-pressed={elegido === h}
                        data-testid="pub-hora"
                      >
                        {fmtHora(h, zona)}
                      </button>
                      </span>
                    ))}
                  </div>
                </>
              )}
      </div>
    </div>
  );
}
